import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

const MODULES = [
  { value: 'customers', label: 'Customers' },
  { value: 'vendors', label: 'Vendors' },
  { value: 'departments', label: 'Departments' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'sales_orders', label: 'Sales Orders' },
  { value: 'purchase_orders', label: 'Purchase Orders' },
  { value: 'indents', label: 'Material Indents' },
  { value: 'production', label: 'Production' },
  { value: 'packing_lists', label: 'Packing Lists' },
  { value: 'labels', label: 'Labels' },
  { value: 'coa', label: 'CoA' },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
];

const CustomFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    module: '', field_name: '', field_label: '', field_type: 'text',
    dropdown_options: [], required: false, show_in_filter: false, show_in_print: false, sort_order: 0
  });
  const [optionInput, setOptionInput] = useState('');

  useEffect(() => { fetchFields(); }, []);

  const fetchFields = async () => {
    try { const res = await api.get('/config/custom-fields'); setFields(res.data); }
    catch (err) { console.error('Failed to fetch:', err); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingField(null);
    setFormData({ module: '', field_name: '', field_label: '', field_type: 'text', dropdown_options: [], required: false, show_in_filter: false, show_in_print: false, sort_order: 0 });
    setOptionInput('');
    setShowModal(true);
  };

  const openEdit = (f) => {
    setEditingField(f);
    setFormData({
      module: f.module, field_name: f.field_name, field_label: f.field_label,
      field_type: f.field_type, dropdown_options: f.dropdown_options || [],
      required: f.required, show_in_filter: f.show_in_filter, show_in_print: f.show_in_print,
      sort_order: f.sort_order
    });
    setOptionInput('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingField) {
        const { module, field_name, ...updateData } = formData;
        await api.put(`/config/custom-fields/${editingField.id}`, updateData);
      } else {
        await api.post('/config/custom-fields', formData);
      }
      setShowModal(false);
      fetchFields();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deleteField = async (id, label) => {
    if (!window.confirm(`Delete custom field "${label}"?`)) return;
    try { await api.delete(`/config/custom-fields/${id}`); fetchFields(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const addOption = () => {
    if (optionInput.trim()) {
      setFormData({ ...formData, dropdown_options: [...formData.dropdown_options, optionInput.trim()] });
      setOptionInput('');
    }
  };

  const removeOption = (idx) => {
    setFormData({ ...formData, dropdown_options: formData.dropdown_options.filter((_, i) => i !== idx) });
  };

  const filtered = fields.filter(f => {
    const matchModule = moduleFilter === 'all' || f.module === moduleFilter;
    const matchSearch = f.field_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.field_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchModule && matchSearch;
  });

  const groupedByModule = {};
  filtered.forEach(f => {
    if (!groupedByModule[f.module]) groupedByModule[f.module] = [];
    groupedByModule[f.module].push(f);
  });

  return (
    <div data-testid="custom-fields-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Custom Fields</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-field-btn"><Plus size={16} /> Add Custom Field</button>
      </div>

      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input type="text" className="form-input" placeholder="Search fields..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} data-testid="module-filter">
              <option value="all">All Modules</option>
              {MODULES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? <div className="card"><div data-testid="loading-indicator">Loading...</div></div> : (
        Object.entries(groupedByModule).map(([mod, modFields]) => (
          <div key={mod} className="card mb-4">
            <div style={{ fontSize: '14px', fontWeight: 700, textTransform: 'capitalize', marginBottom: '12px', color: 'var(--text-primary)' }}>
              {MODULES.find(m => m.value === mod)?.label || mod}
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Field Name</th><th>Label</th><th>Type</th><th>Required</th><th>Filter</th><th>Print</th><th>Order</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {modFields.map(f => (
                    <tr key={f.id} data-testid={`field-row-${f.field_name}`}>
                      <td className="font-mono text-sm">{f.field_name}</td>
                      <td>{f.field_label}</td>
                      <td className="text-sm">{f.field_type}{f.field_type === 'dropdown' && f.dropdown_options?.length > 0 && ` (${f.dropdown_options.length})`}</td>
                      <td className="text-center">{f.required ? 'Yes' : '-'}</td>
                      <td className="text-center">{f.show_in_filter ? 'Yes' : '-'}</td>
                      <td className="text-center">{f.show_in_print ? 'Yes' : '-'}</td>
                      <td className="text-center tabular-nums">{f.sort_order}</td>
                      <td><span className={`badge ${f.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{f.status}</span></td>
                      <td>
                        <div className="flex gap-2">
                          <button onClick={() => openEdit(f)} className="btn btn-ghost btn-sm" data-testid={`edit-btn-${f.field_name}`}><Edit size={14} /></button>
                          <button onClick={() => deleteField(f.id, f.field_label)} className="btn btn-ghost btn-sm" data-testid={`delete-btn-${f.field_name}`}><Trash2 size={14} color="#DC2626" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
      {!loading && Object.keys(groupedByModule).length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
          No custom fields defined yet. Click "Add Custom Field" to get started.
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" data-testid="field-modal">
          <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingField ? 'Edit Custom Field' : 'Add Custom Field'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Module *</label>
                    <select className="form-select" value={formData.module} onChange={(e) => setFormData({ ...formData, module: e.target.value })} required disabled={!!editingField} data-testid="module-select">
                      <option value="">Select module...</option>
                      {MODULES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Field Type *</label>
                    <select className="form-select" value={formData.field_type} onChange={(e) => setFormData({ ...formData, field_type: e.target.value })} data-testid="type-select">
                      {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Field Name * <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>(no spaces, lowercase)</span></label>
                    <input type="text" className="form-input" value={formData.field_name} onChange={(e) => setFormData({ ...formData, field_name: e.target.value.toLowerCase().replace(/\s+/g, '_') })} required disabled={!!editingField} data-testid="field-name-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Label *</label>
                    <input type="text" className="form-input" value={formData.field_label} onChange={(e) => setFormData({ ...formData, field_label: e.target.value })} required data-testid="field-label-input" />
                  </div>
                </div>

                {formData.field_type === 'dropdown' && (
                  <div className="form-group">
                    <label className="form-label">Dropdown Options</label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input type="text" className="form-input" value={optionInput} onChange={(e) => setOptionInput(e.target.value)} placeholder="Add option..." onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }} data-testid="option-input" />
                      <button type="button" onClick={addOption} className="btn btn-secondary" data-testid="add-option-btn">Add</button>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {formData.dropdown_options.map((opt, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: 'var(--bg-surface-alt)', borderRadius: '4px', fontSize: '12px' }}>
                          {opt}
                          <button type="button" onClick={() => removeOption(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#DC2626', fontSize: '14px' }}>&times;</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2" style={{ marginTop: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Sort Order</label>
                    <input type="number" className="form-input" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} data-testid="sort-input" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.required} onChange={(e) => setFormData({ ...formData, required: e.target.checked })} data-testid="required-check" /> Required
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.show_in_filter} onChange={(e) => setFormData({ ...formData, show_in_filter: e.target.checked })} data-testid="filter-check" /> Show in Filter
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.show_in_print} onChange={(e) => setFormData({ ...formData, show_in_print: e.target.checked })} data-testid="print-check" /> Show in Print
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-field-btn">{editingField ? 'Update' : 'Add Field'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFields;
